import { memo } from 'react'
import { Node, Edge } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/shared/components/ui/select"
import { Badge } from "@/shared/components/ui/badge"
import { Slider } from "@/shared/components/ui/slider"
import { Switch } from "@/shared/components/ui/switch"
import { PenLine } from "lucide-react"
import { ToolSelector } from "@/modules/agent/components/ToolSelector"
import { AgentVoiceConfig } from '../AgentVoiceConfig'
import { UserContextSwitch } from '../UserContextSwitch'
import type { ToolListItem, BuiltinToolInfo } from "@/modules/agent/types/tool"

interface ModelOption {
  id: string
  name: string
  description: string
  providerId: string
  providerName: string
  configured?: boolean
}

interface ProviderGroup {
  provider: {
    id: string
    name: string
    configured: boolean
  }
  models: ModelOption[]
}

interface AgentNodeConfigProps {
  selectedNode: Node
  edges: Edge[]
  outputNodes: Node[]
  modelsByProvider: ProviderGroup[]
  defaultModel: string
  tools: ToolListItem[]
  builtinTools: BuiltinToolInfo[]
  isLoadingTools: boolean
  userContextApiConfigured: boolean
  onUpdateNodeData: (field: string, value: unknown) => void
  onOpenPromptEditor: (field: string, content: string, title: string, description: string) => void
}

export const AgentNodeConfig = memo(({
  selectedNode,
  edges,
  outputNodes,
  modelsByProvider,
  defaultModel,
  tools,
  builtinTools,
  isLoadingTools,
  userContextApiConfigured,
  onUpdateNodeData,
  onOpenPromptEditor
}: AgentNodeConfigProps) => {
  
  return (
    <>
      <div className="space-y-2">
        <Label>Agent 名称</Label>
        <Input 
          value={selectedNode.data.label || ''} 
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label>角色定位</Label>
        <Input 
          value={selectedNode.data.role || ''} 
          onChange={(e) => onUpdateNodeData('role', e.target.value)}
          placeholder="例如：客服助手、技术专家、创意顾问"
        />
      </div>

      {/* Prompt 配置 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>提示词</Label>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs gap-1"
            onClick={() => onOpenPromptEditor(
              'prompt',
              selectedNode.data.prompt || '',
              'Agent 提示词',
              '定义 Agent 的具体行为和回复风格'
            )}
          >
            <PenLine className="h-3 w-3" />
            编辑
          </Button>
        </div>
        {selectedNode.data.prompt && (
          <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded border-l-2 border-blue-200 max-h-20 overflow-y-auto">
            {selectedNode.data.prompt.slice(0, 100)}
            {selectedNode.data.prompt.length > 100 && '...'}
          </div>
        )}
      </div>

      {/* 模型选择 */}
      <div className="space-y-2">
        <Label>模型</Label>
        {(() => {
          const currentModel = selectedNode.data.model || defaultModel
          return (
            <Select 
              value={currentModel} 
              onValueChange={(value) => onUpdateNodeData('model', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="选择模型" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {modelsByProvider.length > 0 ? (
                  modelsByProvider.map((group) => (
                    <SelectGroup key={group.provider.id}>
                      <SelectLabel className="flex items-center gap-2">
                        {group.provider.name}
                        {group.provider.configured && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">已配置</Badge>
                        )}
                      </SelectLabel>
                      {group.models.map((model) => (
                        <SelectItem key={model.id} value={model.id} className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            {model.name}
                            {model.id === defaultModel && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700">
                                ⭐ 默认
                              </Badge>
                            )}
                            {model.id === currentModel && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-green-100 text-green-700">
                                ✓ 使用中
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    加载模型列表中...
                  </div>
                )}
              </SelectContent>
            </Select>
          )
        })()}
      </div>

      {/* Temperature 配置 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Temperature</Label>
          <span className="text-sm font-medium text-blue-600">{(selectedNode.data.temperature ?? 0.7).toFixed(1)}</span>
        </div>
        <Slider
          value={selectedNode.data.temperature ?? 0.7}
          onValueChange={(value) => onUpdateNodeData('temperature', Math.round(value * 100) / 100)}
          min={0.0}
          max={2.0}
          step={0.1}
          className="[&_[role=slider]]:bg-blue-500"
        />
        <p className="text-xs text-slate-500">
          控制输出的随机性：0.0=确定性，2.0=高创造性
        </p>
      </div>
      
      {/* 工具选择 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>工具</Label>
          {/* 工具调用模式：仅当选择了工具时显示 */}
          {(selectedNode.data.tools?.length || 0) > 0 && (
            <Select 
              value={selectedNode.data.tool_call_mode || 'json_text'} 
              onValueChange={(v) => onUpdateNodeData('tool_call_mode', v)}
            >
              <SelectTrigger className="h-6 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="function_calling">
                  <span className="flex items-center gap-1">
                    Function Calling
                    <span className="text-[9px] text-green-600 font-medium">推荐</span>
                  </span>
                </SelectItem>
                <SelectItem value="json_text">JSON 文本</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <ToolSelector
          tools={tools}
          builtinTools={builtinTools}
          selectedTools={selectedNode.data.tools || []}
          onToolsChange={(newTools) => onUpdateNodeData('tools', newTools)}
          isLoading={isLoadingTools}
        />

        {/* V5.0/5.1: 工具执行模式（仅当选择了工具时显示） */}
        {(selectedNode.data.tools?.length || 0) > 0 && (
          <div className="pt-3 mt-3 border-t space-y-3">
            {/* V5.1: One-Shot 模式（仅 json_text 模式可用） */}
            {(() => {
              const canUseOneShot = selectedNode.data.tool_call_mode === 'json_text'
              return (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="one-shot-mode-switch" className="text-sm">
                      One-Shot 模式
                    </Label>
                    <p className={`text-xs max-w-[200px] ${canUseOneShot ? 'text-slate-500' : 'text-slate-400'}`}>
                      {canUseOneShot 
                        ? 'LLM 一次输出自然语言+工具调用，响应更快' 
                        : '需要选择 JSON Text 调用模式'}
                    </p>
                  </div>
                  <Switch
                    id="one-shot-mode-switch"
                    checked={selectedNode.data.one_shot_mode ?? false}
                    onCheckedChange={(checked) => onUpdateNodeData('one_shot_mode', checked)}
                    disabled={!canUseOneShot}
                  />
                </div>
              )
            })()}

            {/* V5.0: 异步模式 */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="async-mode-switch" className="text-sm">
                  异步模式
                </Label>
                <p className="text-xs text-slate-500 max-w-[200px]">
                  工具 Fire-and-Forget，立即返回模板回复
                </p>
              </div>
              <Switch
                id="async-mode-switch"
                checked={selectedNode.data.async_mode ?? false}
                onCheckedChange={(checked) => onUpdateNodeData('async_mode', checked)}
              />
            </div>

            {/* V5.0: 异步回复模板（仅异步模式时显示） */}
            {selectedNode.data.async_mode && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>异步回复模板</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs gap-1"
                    onClick={() => onOpenPromptEditor(
                      'async_response_template',
                      selectedNode.data.async_response_template || '',
                      '异步回复模板',
                      '工具异步执行时的即时回复内容'
                    )}
                  >
                    <PenLine className="h-3 w-3" />
                    编辑
                  </Button>
                </div>
                {selectedNode.data.async_response_template && (
                  <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded border-l-2 border-blue-200 max-h-20 overflow-y-auto">
                    {selectedNode.data.async_response_template.slice(0, 100)}
                    {selectedNode.data.async_response_template.length > 100 && '...'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 静默模式 */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="silent-mode-switch" className="text-sm">
            静默模式
          </Label>
          <p className="text-xs text-slate-500 max-w-[200px]">
            不向用户显示此节点的输出
          </p>
        </div>
        <Switch
          id="silent-mode-switch"
          checked={selectedNode.data.silent_mode ?? false}
          onCheckedChange={(checked) => onUpdateNodeData('silent_mode', checked)}
        />
      </div>

      {/* 工具调用流式输出 */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label htmlFor="stream-tool-calls-switch" className="text-sm">
            工具调用流式输出
          </Label>
          <p className="text-xs text-slate-500 max-w-[200px]">
            实时流式返回工具调用过程
          </p>
        </div>
        <Switch
          id="stream-tool-calls-switch"
          checked={selectedNode.data.enable_stream_tool_calls ?? false}
          onCheckedChange={(checked) => onUpdateNodeData('enable_stream_tool_calls', checked)}
        />
      </div>

      {/* 用户上下文开关 */}
      <UserContextSwitch
        selectedNode={selectedNode}
        onUpdateNodeData={onUpdateNodeData}
        userContextApiConfigured={userContextApiConfigured}
        standalone={true}
      />

      {/* Agent 语音配置 */}
      <AgentVoiceConfig
        selectedNode={selectedNode}
        edges={edges}
        outputNodes={outputNodes}
        onUpdateNodeData={onUpdateNodeData}
      />
    </>
  )
})

AgentNodeConfig.displayName = 'AgentNodeConfig'